import { useState } from 'react';
import {
  Play,
  RotateCcw,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  Save,
  History,
  AlertTriangle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import type { WorkflowNode } from '@/types';

interface TestCase {
  id: string;
  name: string;
  input: string;
  expectedOutput?: string;
  createdAt: Date;
}

interface TestResult {
  id: string;
  testCaseId: string;
  status: 'pending' | 'running' | 'passed' | 'failed';
  input: string;
  output?: string;
  expectedOutput?: string;
  executionTime?: number;
  nodeResults?: Array<{
    nodeId: string;
    nodeLabel: string;
    status: 'passed' | 'failed' | 'skipped' | 'mocked';
    output?: string;
    error?: string;
    isMocked?: boolean;
  }>;
  error?: string;
  createdAt: Date;
}

interface WorkflowTestPanelProps {
  nodes: WorkflowNode[];
  onNodeStatusChange?: (nodeId: string, status: WorkflowNode['data']['status']) => void;
  onRunTest?: (input: string) => Promise<string>;
  className?: string;
}

export function WorkflowTestPanel({
  nodes,
  onNodeStatusChange,
  onRunTest,
  className,
}: WorkflowTestPanelProps) {
  const [input, setInput] = useState('{\n  "message": "Hello"\n}');
  const [isRunning, setIsRunning] = useState(false);
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [savedTestCases, setSavedTestCases] = useState<TestCase[]>([]);
  const [selectedTestCase, setSelectedTestCase] = useState<string | null>(null);
  const [showLogs, setShowLogs] = useState(true);
  const [expectedOutput, setExpectedOutput] = useState('');

  // Find nodes that need mocking (unsupported features)
  const mockedNodes = nodes.filter(
    (n) => n.data.status === 'pending' && n.data.type !== 'start' && n.data.type !== 'end'
  );

  const handleRunTest = async () => {
    setIsRunning(true);

    const newResult: TestResult = {
      id: crypto.randomUUID(),
      testCaseId: selectedTestCase || 'adhoc',
      status: 'running',
      input,
      expectedOutput: expectedOutput || undefined,
      nodeResults: [],
      createdAt: new Date(),
    };

    setTestResults((prev) => [newResult, ...prev]);

    try {
      // Validate JSON input
      let parsedInput;
      try {
        parsedInput = JSON.parse(input);
      } catch {
        throw new Error('Invalid JSON input');
      }

      // Simulate workflow execution through nodes
      const nodeResults: TestResult['nodeResults'] = [];
      const startTime = Date.now();

      for (const node of nodes) {
        if (node.data.type === 'start') continue;

        // Update node status to running
        onNodeStatusChange?.(node.id, 'running');
        await new Promise((resolve) => setTimeout(resolve, 300));

        const isMockedNode = mockedNodes.some((m) => m.id === node.id);

        if (isMockedNode) {
          // Mock the unsupported node
          nodeResults.push({
            nodeId: node.id,
            nodeLabel: node.data.label,
            status: 'mocked',
            output: JSON.stringify({
              mock: true,
              message: `Mocked response for ${node.data.label}`,
            }),
            isMocked: true,
          });
          onNodeStatusChange?.(node.id, 'completed');
        } else {
          // Execute the node (simulation)
          try {
            nodeResults.push({
              nodeId: node.id,
              nodeLabel: node.data.label,
              status: 'passed',
              output: JSON.stringify({ processed: true }),
            });
            onNodeStatusChange?.(node.id, 'completed');
          } catch (e) {
            nodeResults.push({
              nodeId: node.id,
              nodeLabel: node.data.label,
              status: 'failed',
              error: e instanceof Error ? e.message : 'Unknown error',
            });
            onNodeStatusChange?.(node.id, 'failed');
            throw e;
          }
        }

        await new Promise((resolve) => setTimeout(resolve, 200));
      }

      const executionTime = Date.now() - startTime;

      // Generate output
      let output: string;
      if (onRunTest) {
        output = await onRunTest(input);
      } else {
        // Mock output
        output = JSON.stringify(
          {
            status: 'success',
            result: {
              processed: true,
              input: parsedInput,
              mockedNodes: mockedNodes.map((n) => n.data.label),
            },
          },
          null,
          2
        );
      }

      // Check if output matches expected
      const isPassed = expectedOutput
        ? output.trim() === expectedOutput.trim()
        : true;

      setTestResults((prev) =>
        prev.map((r) =>
          r.id === newResult.id
            ? {
                ...r,
                status: isPassed ? 'passed' : 'failed',
                output,
                nodeResults,
                executionTime,
              }
            : r
        )
      );
    } catch (e) {
      setTestResults((prev) =>
        prev.map((r) =>
          r.id === newResult.id
            ? {
                ...r,
                status: 'failed',
                error: e instanceof Error ? e.message : 'Unknown error',
              }
            : r
        )
      );
    } finally {
      setIsRunning(false);
    }
  };

  const handleSaveTestCase = () => {
    const newTestCase: TestCase = {
      id: crypto.randomUUID(),
      name: `Test Case ${savedTestCases.length + 1}`,
      input,
      expectedOutput: expectedOutput || undefined,
      createdAt: new Date(),
    };
    setSavedTestCases((prev) => [newTestCase, ...prev]);
  };

  const handleLoadTestCase = (testCaseId: string) => {
    const testCase = savedTestCases.find((t) => t.id === testCaseId);
    if (testCase) {
      setInput(testCase.input);
      setExpectedOutput(testCase.expectedOutput || '');
      setSelectedTestCase(testCaseId);
    }
  };

  const handleReset = () => {
    setInput('{\n  "message": "Hello"\n}');
    setExpectedOutput('');
    setSelectedTestCase(null);
    // Reset node statuses
    nodes.forEach((node) => {
      if (node.data.type !== 'start') {
        onNodeStatusChange?.(node.id, 'pending');
      }
    });
  };

  const latestResult = testResults[0];

  return (
    <div className={cn('flex h-full flex-col border-l bg-background', className)}>
      {/* Header */}
      <div className="flex items-center justify-between border-b px-4 py-2">
        <div className="flex items-center gap-2">
          <Play className="h-5 w-5 text-primary" />
          <span className="font-medium">Test Panel</span>
        </div>
        <div className="flex items-center gap-1">
          {savedTestCases.length > 0 && (
            <Select value={selectedTestCase || ''} onValueChange={handleLoadTestCase}>
              <SelectTrigger className="h-8 w-32">
                <SelectValue placeholder="Load test" />
              </SelectTrigger>
              <SelectContent>
                {savedTestCases.map((tc) => (
                  <SelectItem key={tc.id} value={tc.id}>
                    {tc.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button variant="ghost" size="icon" onClick={handleReset} title="Reset">
            <RotateCcw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Mocked Nodes Warning */}
      {mockedNodes.length > 0 && (
        <div className="border-b bg-orange-50 px-4 py-2 dark:bg-orange-950/30">
          <div className="flex items-center gap-2 text-sm text-orange-600 dark:text-orange-400">
            <AlertTriangle className="h-4 w-4" />
            <span>{mockedNodes.length} node(s) will be mocked</span>
          </div>
          <div className="mt-1 flex flex-wrap gap-1">
            {mockedNodes.map((node) => (
              <Badge key={node.id} variant="outline" className="text-xs">
                {node.data.label}
              </Badge>
            ))}
          </div>
        </div>
      )}

      <ScrollArea className="flex-1">
        <div className="space-y-4 p-4">
          {/* Input Section */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Input (JSON)</Label>
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder='{"message": "Hello"}'
              className="min-h-[120px] font-mono text-sm"
            />
          </div>

          {/* Expected Output Section */}
          <Collapsible>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="w-full justify-between">
                Expected Output (optional)
                <ChevronDown className="h-4 w-4" />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-2">
              <Textarea
                value={expectedOutput}
                onChange={(e) => setExpectedOutput(e.target.value)}
                placeholder="Leave empty to skip validation"
                className="min-h-[100px] font-mono text-sm"
              />
            </CollapsibleContent>
          </Collapsible>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button
              onClick={handleRunTest}
              disabled={isRunning || !input.trim()}
              className="flex-1"
            >
              {isRunning ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <Play className="mr-2 h-4 w-4" />
                  Run Test
                </>
              )}
            </Button>
            <Button variant="outline" onClick={handleSaveTestCase}>
              <Save className="mr-2 h-4 w-4" />
              Save
            </Button>
          </div>

          {/* Latest Result */}
          {latestResult && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Result</Label>
                <Badge
                  variant={
                    latestResult.status === 'passed'
                      ? 'default'
                      : latestResult.status === 'failed'
                        ? 'destructive'
                        : 'secondary'
                  }
                >
                  {latestResult.status === 'running' && (
                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                  )}
                  {latestResult.status === 'passed' && (
                    <CheckCircle className="mr-1 h-3 w-3" />
                  )}
                  {latestResult.status === 'failed' && (
                    <XCircle className="mr-1 h-3 w-3" />
                  )}
                  {latestResult.status}
                </Badge>
              </div>

              {latestResult.output && (
                <div className="rounded-lg border bg-muted/50 p-3">
                  <pre className="text-xs overflow-auto whitespace-pre-wrap">
                    {latestResult.output}
                  </pre>
                </div>
              )}

              {latestResult.error && (
                <div className="rounded-lg border border-destructive bg-destructive/10 p-3">
                  <p className="text-sm text-destructive">{latestResult.error}</p>
                </div>
              )}

              {latestResult.executionTime && (
                <p className="text-xs text-muted-foreground">
                  Execution time: {latestResult.executionTime}ms
                </p>
              )}

              {/* Node Results */}
              {latestResult.nodeResults && latestResult.nodeResults.length > 0 && (
                <Collapsible open={showLogs} onOpenChange={setShowLogs}>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" size="sm" className="w-full justify-between">
                      <span className="flex items-center gap-2">
                        <History className="h-4 w-4" />
                        Node Execution Log
                      </span>
                      {showLogs ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="pt-2">
                    <div className="space-y-2">
                      {latestResult.nodeResults.map((nr, idx) => (
                        <div
                          key={nr.nodeId}
                          className={cn(
                            'rounded border p-2 text-sm',
                            nr.status === 'passed' && 'border-green-200 bg-green-50',
                            nr.status === 'failed' && 'border-red-200 bg-red-50',
                            nr.status === 'mocked' && 'border-orange-200 bg-orange-50'
                          )}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium">
                              {idx + 1}. {nr.nodeLabel}
                            </span>
                            <Badge
                              variant={
                                nr.status === 'passed'
                                  ? 'default'
                                  : nr.status === 'mocked'
                                    ? 'outline'
                                    : 'destructive'
                              }
                              className="text-xs"
                            >
                              {nr.isMocked && '(mocked) '}
                              {nr.status}
                            </Badge>
                          </div>
                          {nr.output && (
                            <pre className="mt-1 text-xs text-muted-foreground overflow-auto">
                              {nr.output}
                            </pre>
                          )}
                          {nr.error && (
                            <p className="mt-1 text-xs text-destructive">{nr.error}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              )}
            </div>
          )}

          {/* Test History */}
          {testResults.length > 1 && (
            <div className="space-y-2">
              <Label className="text-sm font-medium">History</Label>
              <div className="space-y-1">
                {testResults.slice(1, 6).map((result) => (
                  <div
                    key={result.id}
                    className="flex items-center justify-between rounded border p-2 text-sm"
                  >
                    <div className="flex items-center gap-2">
                      {result.status === 'passed' ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : result.status === 'failed' ? (
                        <XCircle className="h-4 w-4 text-red-500" />
                      ) : (
                        <Clock className="h-4 w-4 text-yellow-500" />
                      )}
                      <span className="text-muted-foreground">
                        {result.createdAt.toLocaleTimeString()}
                      </span>
                    </div>
                    {result.executionTime && (
                      <span className="text-xs text-muted-foreground">
                        {result.executionTime}ms
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
